import React, { useState, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { Button, ButtonText, Modal, ModalHeader, ModalContent } from '@unbridaled/ui-components'
import { ArrowLeftIcon } from '@unbridaled/icons/ArrowLeftIcon'
import { InputOtp } from '../inputOtp'
import StoreFunctions from 'stores/storeFunctions'

const MS_SECOND = 1000
const MS_MINUTE = MS_SECOND * 60
const CODE_LENGTH = 6

interface LoginModalProps {
  loginModalConfig: {
    open: boolean
    title?: string
    confirm?: string
    discard?: string
    placeholder?: string
    mode: string
    value?: string
    email?: string
    methodId?: string
    loadingOTP?: boolean
    error?: string | null
  }
  onClose: (action?: string) => void
  onConfirm: (mode: string) => void
  onChange: (value: string, mode: string) => void
  [key: string]: any
}

const storeFunctions = new StoreFunctions()

export const LoginModal: React.FC<LoginModalProps> = ({
  loginModalConfig,
  onClose,
  onConfirm,
  onChange,
  ...props
}) => {
  const { t } = useTranslation()
  const [isResetCodeDisabled, setIsResetCodeDisabled] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [counter, setCounter] = useState<{
    time: number
    intervalId: NodeJS.Timeout | null
  }>({
    time: 0,
    intervalId: null
  })

  const prevModeRef = useRef<string>(loginModalConfig.mode)

  const initiateCountDown = (): NodeJS.Timeout => {
    if (counter.intervalId) clearInterval(counter.intervalId)
    setCounter({ time: MS_MINUTE * 5, intervalId: null })
    const nextIntervalId = setInterval(() => {
      setCounter(interval => {
        const left = interval.time - MS_SECOND
        if (left < 0) clearInterval(nextIntervalId)
        return {
          intervalId: left < 0 ? null : nextIntervalId,
          time: left < 0 ? 0 : left
        }
      })
    }, MS_SECOND)
    return nextIntervalId
  }

  const handleResendCode = (mode: string): void => {
    initiateCountDown()
    setIsResetCodeDisabled(true)
    onConfirm(mode)
    setTimeout(() => setIsResetCodeDisabled(false), MS_SECOND * 10)
  }

  useEffect(() => {
    const intervalId = initiateCountDown()
    return (): void => clearInterval(intervalId)
  }, [])

  useEffect(() => {
    if (loginModalConfig.mode === 'code' && loginModalConfig.value?.length === CODE_LENGTH) onConfirm(loginModalConfig.mode)
  }, [loginModalConfig.value])

  useEffect(() => {
    if (prevModeRef.current === 'email' && loginModalConfig.mode === 'code') {
      initiateCountDown()
    }
    prevModeRef.current = loginModalConfig.mode
  }, [loginModalConfig.mode])

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === 'Enter' && !isSubmitting) {
      setIsSubmitting(true)
      onConfirm(loginModalConfig.mode)
      setTimeout(() => setIsSubmitting(false), 3000)
    }
  }

  const handleClose = (): void => {
    const action = loginModalConfig.mode === 'email' ? undefined : 'back'
    onClose(action)
  }

  const minute = Math.floor(counter.time / MS_MINUTE)
  const second = (counter.time - MS_MINUTE * minute) / MS_SECOND

  return (
    <Modal
      open={loginModalConfig.open}
      onClose={onClose}
      className="unbridaled-loginPopupStyle"
      {...props}
    >
      <ModalHeader
        className="unbridaled-popup-title unbridaled-login-title"
        onClose={onClose}
        leadingIcon={
          loginModalConfig.mode === 'code'
            ? (): React.JSX.Element => (
                <ArrowLeftIcon
                  data-testid="back-button"
                  color="text-primary"
                  className="hover:!opacity-100 transition-colors mr-2.5"
                  onClick={handleClose}
                  size={26}
                />
              )
            : null
        }
      >
        {loginModalConfig.title}
      </ModalHeader>
      <ModalContent className="unbridaled-confirmation-content-wrapper-modal login-modal">
        <div className="unbridaled-confirmation-content-wrapper">
          <div className="unbridaled-confirmation-content-wrapper-input-wrapper">
            {loginModalConfig.mode === 'email' && (
              <>
                <label className="unbridaled-confirmation-content-wrapper-input-wrapper-label">
                  {t('loginModal.emailAddress')}
                </label>
                <input
                  type={loginModalConfig.mode === 'email' ? 'email' : 'text'}
                  className="unbridaled-passwordInputStyle"
                  placeholder={loginModalConfig.placeholder}
                  onChange={(e): void => onChange(e.target.value, loginModalConfig.mode)}
                  value={loginModalConfig.value}
                  onKeyDown={handleKeyDown}
                />
              </>
            )}
            {loginModalConfig.mode === 'code' && (
              <>
                <p className="unbridaled-confirmation-content-wrapper-text">
                  {t('loginModal.codeSentTo')}{' '}
                  <span className="unbridaled-font-bold">
                    {storeFunctions.maskEmail(loginModalConfig.email ?? '')}
                  </span>
                </p>
                <InputOtp
                  onChange={(code): void => onChange(code, loginModalConfig.mode)}
                  maxLength={CODE_LENGTH}
                />
              </>
            )}
            {loginModalConfig.mode === 'code' && (
              <p className="unbridaled-confirmation-content-wrapper-text">
                {t('loginModal.codeExpiresIn')}{' '}
                <span className="unbridaled-font-bold">
                  {minute}:{second < 10 ? `0${second}` : second}
                </span>
                .{' '}
                <ButtonText
                  disabled={isResetCodeDisabled}
                  className="uui-inline-flex uui-items-center unbridaled-confirmation-content-wrapper-input-wrapper-button"
                  onClick={(): void => handleResendCode('email')}
                >
                  {t('loginModal.resendCode')}{' '}
                </ButtonText>
              </p>
            )}
          </div>
          <p className="unbridaled-code-resend-notice">
            {loginModalConfig.error
              ? loginModalConfig.error
              : isResetCodeDisabled
              ? t('loginModal.codeSentSuccessfully')
              : '\u00a0'}
          </p>
        </div>
        <div className="unbridaled-popup-footer uui-gap-4 uui-flex uui-justify-end uui-border-t uui-border-primary">
          <Button
            className="uui-flex-1 unbridaled-submit"
            onClick={(): void => onConfirm(loginModalConfig.mode)}
            {...(loginModalConfig.loadingOTP !== undefined
              ? { loading: loginModalConfig.loadingOTP }
              : {})}
            disabled={
              (loginModalConfig.mode === 'email' && loginModalConfig.value?.length === 0) ||
              (loginModalConfig.mode === 'code' && loginModalConfig.value?.length !== CODE_LENGTH) ||
              isSubmitting
            }
          >
            {loginModalConfig.confirm}
          </Button>
        </div>
      </ModalContent>
    </Modal>
  )
}

export default LoginModal
